// buildUserBalanceConfigFromData.ts
import {
  isVisibleVesBank,
} from '../constants/visible-ves-banks';

type BalanceWallet = {
  wallet_id: string;
  ledger_wallet_id?: string;
  total: string;
};
type BalanceItem = {
  coin_id: string;
  total?: string;
  wallets?: BalanceWallet[];
};

type CurrencyType = { id: number; name: string };

type TransactionType = { id: string; currency: any }; // currency puede ser string o array/objeto JSON

type ConfigCurrency = {
  id?: string;
  name?: string | null;
  shortname?: string | null;
  description?: string | null;
  coin_type_id?: number | null;
  hardness_rank?: number | null;
  currency_type?: CurrencyType | null;
  currency?: string;
  services: any[];
  subwallets: Array<{
    id: string;
    name: string;
    shortname: string;
    metadata: any;
    coin_id: string;
  }>;
};

type ConfigData = {
  currencies: ConfigCurrency[];
  services: any[];
  roles: any[];
  currency_types?: CurrencyType[];
  transaction_types?: TransactionType[];
};

type UserBalanceConfig = {
  currencies: Array<
    ConfigCurrency & {
      currency: string;
      balance: { total: string };
      subwallets: Array<
        ConfigCurrency['subwallets'][number] & { balance: string }
      >;
      currency_type?: CurrencyType | null;
    }
  >;
  services: any[];
  roles: any[];
  currency_types: CurrencyType[];
  transaction_types: TransactionType[];
};

// helpers
const norm = (s: unknown) => (s ?? '').toString().trim().toUpperCase();
const f4 = (n: unknown) => {
  const v = typeof n === 'string' ? Number(n) : ((n as number) ?? 0);
  return (Number.isFinite(v) ? (v as number) : 0).toFixed(10);
};
const add = (a: number, b: unknown) => {
  const nb = typeof b === 'string' ? parseFloat(b) : ((b as number) ?? 0);
  return a + (Number.isFinite(nb) ? nb : 0);
};

export function buildUserBalanceConfigFromData(
  configData: ConfigData,
  balanceData: BalanceItem[],
  injectUnknownWallets = false,
): UserBalanceConfig {
  // ----- índices de balance
  const byCoin = new Map<string, BalanceItem>();
  const byCoinWallet = new Map<string, Map<string, string>>();
  (balanceData ?? []).forEach((b) => {
    const coinKey = norm(b.coin_id);
    byCoin.set(coinKey, { ...b, coin_id: coinKey });
    const wmap = new Map<string, string>();
    (b.wallets ?? []).forEach((w) =>
      wmap.set(norm(w.ledger_wallet_id || w.wallet_id), w.total),
    );
    byCoinWallet.set(coinKey, wmap);
  });

  // ----- índice de currency_types para fallbacks
  const currencyTypes = configData.currency_types ?? [];
  const currencyTypeById = new Map<number, CurrencyType>();
  currencyTypes.forEach((ct) => {
    if (ct && typeof ct.id === 'number') currencyTypeById.set(ct.id, ct);
  });

  const currencies = (configData.currencies ?? []).map((cur) => {
    const idRaw = cur.currency ?? cur.id ?? '';
    const coinKey = norm(idRaw);

    const b = byCoin.get(coinKey);
    const walletTotals = byCoinWallet.get(coinKey) ?? new Map<string, string>();

    let sumAll = 0;
    const seen = new Set<string>();

    const subwallets = (cur.subwallets ?? [])
      .filter((sw) => coinKey !== 'VES' || isVisibleVesBank(sw.id))
      .map((sw) => {
      const idKey = norm(sw.id);
      seen.add(idKey);
      const wTotal = walletTotals.get(idKey) ?? '0';
      sumAll = add(sumAll, wTotal);
      return { ...sw, balance: f4(wTotal) };
      });

    if (injectUnknownWallets && walletTotals.size) {
      walletTotals.forEach((val, wid) => {
        if (!seen.has(wid) && (coinKey !== 'VES' || isVisibleVesBank(wid))) {
          sumAll = add(sumAll, val);
          subwallets.push({
            id: wid,
            name: wid,
            shortname: wid,
            metadata: {},
            coin_id: coinKey,
            balance: f4(val),
          });
        }
      });
    }

    const total = coinKey === 'VES' ? f4(sumAll) : b?.total != null ? f4(b.total) : f4(sumAll);

    const currency_type =
      cur.currency_type ??
      (typeof cur.coin_type_id === 'number'
        ? (currencyTypeById.get(cur.coin_type_id) ?? null)
        : null);

    return {
      id: cur.id ?? coinKey,
      name: cur.name ?? null,
      shortname: cur.shortname ?? null,
      description: cur.description ?? null,
      coin_type_id: cur.coin_type_id ?? null,
      hardness_rank: cur.hardness_rank ?? null,
      currency_type,
      currency: idRaw || coinKey,
      balance: { total },
      services: cur.services ?? [],
      subwallets,
    };
  });

  return {
    currencies,
    services: configData.services ?? [],
    roles: configData.roles ?? [],
    currency_types: currencyTypes,
    transaction_types: configData.transaction_types ?? [], // <--- agregado aquí
  };
}

export const BITSO_MXN_SUBWALLET: ConfigCurrency['subwallets'][number] = {
  id: 'BITSO_MXN',
  name: 'BITSO MXN',
  shortname: 'Bitso',
  coin_id: 'MXN',
  metadata: {
    provider: 'bitso',
    description: 'Bitso para transacciones MXN',
  },
};

const DEPA_FIAT_CURRENCIES = ['EUR', 'GBP', 'USD'];

/** Subwallet de balance Depasify (mismo id que usa el backend en assign/cashout). */
export function createDepaSubwallet(
  currency: string,
): ConfigCurrency['subwallets'][number] {
  const coin = normalizeCurrencyCode(currency);
  return {
    id: 'DEPA',
    name: 'Depasify',
    shortname: 'Depasify',
    coin_id: coin,
    metadata: {
      provider: 'depa',
      description: 'Saldo acreditado vía Depasify',
    },
  };
}

function withDepaFiatSubwalletFallback(
  currency: string,
  subwallets: ConfigCurrency['subwallets'],
): ConfigCurrency['subwallets'] {
  const key = normalizeCurrencyCode(currency);
  if (!DEPA_FIAT_CURRENCIES.includes(key)) {
    return subwallets;
  }
  const hasDepa = subwallets.some(
    (sw) => normalizeCurrencyCode(sw.id) === 'DEPA',
  );
  if (hasDepa) {
    return subwallets;
  }
  return [...subwallets, createDepaSubwallet(key)];
}

export function normalizeCurrencyCode(raw: unknown): string {
  return (raw ?? '').toString().trim().toUpperCase();
}

export function findCurrencyInConfig(
  currencies: ConfigCurrency[] | undefined,
  currency: string,
): ConfigCurrency | undefined {
  const key = normalizeCurrencyCode(currency);
  if (!key || !currencies?.length) {
    return undefined;
  }

  return currencies.find((el) => {
    const id = normalizeCurrencyCode(el.id);
    const code = normalizeCurrencyCode(el.currency);
    return id === key || code === key;
  });
}

/** Subwallets para dropdowns de cashin/cashout; cae a config global y fallback MXN. */
export function getSubwalletsForCurrency(
  currency: string | null | undefined,
  userBalanceConfig?: { currencies?: ConfigCurrency[] },
  appConfig?: { currencies?: ConfigCurrency[] },
): ConfigCurrency['subwallets'] {
  const key = normalizeCurrencyCode(currency);
  if (!key) {
    return [];
  }

  const fromUser = findCurrencyInConfig(userBalanceConfig?.currencies, key);
  if (fromUser?.subwallets?.length) {
    return withDepaFiatSubwalletFallback(key, fromUser.subwallets);
  }

  const fromApp = findCurrencyInConfig(appConfig?.currencies, key);
  if (fromApp?.subwallets?.length) {
    return withDepaFiatSubwalletFallback(key, fromApp.subwallets);
  }

  if (key === 'MXN') {
    return [{ ...BITSO_MXN_SUBWALLET }];
  }

  if (DEPA_FIAT_CURRENCIES.includes(key)) {
    return [createDepaSubwallet(key)];
  }

  return [];
}
